// ===== Packages =====

import {
    FirebaseError,
}                                           from 'firebase/app';
import {
    EmailAuthProvider,
    User,
    fetchSignInMethodsForEmail,
    linkWithCredential,
}                                           from 'firebase/auth';

// ===== Firebase =====

import fire                                 from '../fire';

// ===== Services =====

import {
    signIn,
    signUpWithEmail,
    getAuthErrorMessage,
    isEmailUnusedByOtherUsers,
    getExistingEmailSignInGuidance,
    getAuthenticationTypeLabel,
    mapSignInMethodToAuthenticationType,
    createAuthFlowError,
    formatProviderList,
}                                           from '.';
import {
    EMAIL_PASSWORD_SIGN_IN_LABEL,
}                                           from './authProviderHelpers';

// ===== Interfaces =====

import {
    IAuthenticatedUserSetupOldUserData,
    IAuthFlowError,
}                                           from '../interfaces';

// ===== Enums =====

import {
    AUTHENTICATION_ERROR_CODE,
    AUTHENTICATION_TYPE,
    EMAIL_CONVERSION_CALLABLE_ERROR_CODE,
    EMAIL_CREDENTIALS_AUTH_MODE,
}                                           from '../enums';

// ===== General Constants =====

import {
    EMAIL_SIGN_UP_ERROR,
    EMAIL_SIGN_UP_EMAIL_IN_USE,
    EMAIL_SIGN_IN_ERROR,
    EMAIL_SIGN_IN_ACCOUNT_NOT_FOUND,
    EMAIL_SIGN_IN_INVALID_CREDENTIALS,
    EMAIL_SIGN_IN_WRONG_PROVIDER,
    EMAIL_SIGN_UP_PRECHECK_ERROR,
    FETCH_SIGN_IN_METHODS_RETRY_DELAY_MS,
}                                           from '../constants/generalConstants';

const { auth } = fire();

const wait = async (duration: number): Promise<void> => new Promise((resolve) => {
    setTimeout(resolve, duration);
});

const fetchSignInMethodsWithRetry = async (email: string): Promise<string[]> => {
    try {
        return await fetchSignInMethodsForEmail(auth, email);
    } catch {
        await wait(FETCH_SIGN_IN_METHODS_RETRY_DELAY_MS);
        return fetchSignInMethodsForEmail(auth, email);
    }
};

const resolveWrongProviderMessage = async (email: string): Promise<string> => {
    try {
        const methods = await fetchSignInMethodsWithRetry(email);
        const providerLabels = Array.from(
            new Set(
                methods
                    .map((method) => {
                        const authenticationType = mapSignInMethodToAuthenticationType(method);
                        if (!authenticationType) return method;
                        return getAuthenticationTypeLabel(authenticationType);
                    })
                    .filter((method) => method !== EMAIL_PASSWORD_SIGN_IN_LABEL),
            ),
        );
        if (providerLabels.length === 0) return EMAIL_SIGN_IN_WRONG_PROVIDER;
        const providerList = formatProviderList(providerLabels);
        return (
            `This email is already linked to ${providerList}. `
            + `Please sign in with ${providerList}.`
        );
    } catch {
        return EMAIL_SIGN_IN_WRONG_PROVIDER;
    }
};

const resolveEmailSignInErrorMessage = async ({
    test,
    email,
    error,
}: {
    test: boolean,
    email: string,
    error: FirebaseError,
}): Promise<string> => {
    const authErrorCode = error.code as AUTHENTICATION_ERROR_CODE;

    if (authErrorCode === AUTHENTICATION_ERROR_CODE.userNotFound) {
        try {
            const emailUnusedByOtherUsers = await isEmailUnusedByOtherUsers({
                test,
                email,
            });
            if (!emailUnusedByOtherUsers) {
                return resolveWrongProviderMessage(email);
            }
        } catch {
            return EMAIL_SIGN_IN_WRONG_PROVIDER;
        }
        return EMAIL_SIGN_IN_ACCOUNT_NOT_FOUND;
    }

    if (
        authErrorCode === AUTHENTICATION_ERROR_CODE.invalidCredential
        || authErrorCode === AUTHENTICATION_ERROR_CODE.wrongPassword
    ) {
        return EMAIL_SIGN_IN_INVALID_CREDENTIALS;
    }

    if (
        authErrorCode === AUTHENTICATION_ERROR_CODE.accountExistsWithDifferentCredential
        || authErrorCode === AUTHENTICATION_ERROR_CODE.credentialAlreadyInUse
    ) {
        return resolveWrongProviderMessage(email);
    }

    return getAuthErrorMessage(authErrorCode)
        || EMAIL_SIGN_IN_ERROR;
};

const signInWithEmailCredentials = async ({
    test,
    hasSound,
    email,
    password,
    oldUserData,
}: {
    test: boolean,
    hasSound: boolean,
    email: string,
    password: string,
    oldUserData?: IAuthenticatedUserSetupOldUserData,
}): Promise<User | null> => new Promise((resolve, reject) => {
    signIn({
        type: AUTHENTICATION_TYPE.email,
        hasSound,
        signingInOut: false,
        oldUserData: {
            user: oldUserData?.user,
            email: oldUserData?.email,
            oldUserCart: oldUserData?.oldUserCart,
            userSessions: oldUserData?.userSessions,
            userUpdateTimestamps: oldUserData?.userUpdateTimestamps,
        },
        credentials: {
            email,
            password,
        },
        onComplete: (currentUser) => resolve(currentUser),
        onError: (error) => {
            resolveEmailSignInErrorMessage({
                test,
                email,
                error,
            }).then((message) => {
                reject(createAuthFlowError<IAuthFlowError>(message, error.code));
            });
        },
    });
});

const signUpWithEmailCredentials = async ({
    test,
    email,
    password,
    oldUserData,
}: {
    test: boolean,
    email: string,
    password: string,
    oldUserData?: IAuthenticatedUserSetupOldUserData,
}): Promise<User> => {
    try {
        const emailUnusedByOtherUsers = await isEmailUnusedByOtherUsers({
            test,
            email,
        });
        if (!emailUnusedByOtherUsers) {
            const existingEmailMessage = await getExistingEmailSignInGuidance({
                email,
                fallback: EMAIL_SIGN_UP_EMAIL_IN_USE,
            });
            throw createAuthFlowError<IAuthFlowError>(
                existingEmailMessage,
                AUTHENTICATION_ERROR_CODE.emailAlreadyInUse,
            );
        }
    } catch (error) {
        if ((error as IAuthFlowError)?.code === AUTHENTICATION_ERROR_CODE.emailAlreadyInUse) {
            throw error;
        }
        throw createAuthFlowError<IAuthFlowError>(
            EMAIL_SIGN_UP_PRECHECK_ERROR,
            EMAIL_CONVERSION_CALLABLE_ERROR_CODE.precheckUnavailable,
        );
    }

    const shouldLinkAnonymous = (
        !!auth.currentUser
        && auth.currentUser.isAnonymous
        && !!oldUserData?.user
        && oldUserData.user.id === auth.currentUser.uid
    );

    if (shouldLinkAnonymous && auth.currentUser) {
        try {
            const credential = EmailAuthProvider.credential(email, password);
            const linkedResult = await linkWithCredential(auth.currentUser, credential);
            return linkedResult.user;
        } catch (error) {
            const authError = error as FirebaseError;
            const authErrorCode = authError.code as AUTHENTICATION_ERROR_CODE;
            if (
                authErrorCode === AUTHENTICATION_ERROR_CODE.emailAlreadyInUse
                || authErrorCode === AUTHENTICATION_ERROR_CODE.emailAlreadyExists
                || authErrorCode === AUTHENTICATION_ERROR_CODE.accountExistsWithDifferentCredential
                || authErrorCode === AUTHENTICATION_ERROR_CODE.credentialAlreadyInUse
            ) {
                const existingEmailMessage = await getExistingEmailSignInGuidance({
                    email,
                    fallback: EMAIL_SIGN_UP_EMAIL_IN_USE,
                });
                throw createAuthFlowError<IAuthFlowError>(existingEmailMessage, authError.code);
            }
            throw createAuthFlowError<IAuthFlowError>(
                getAuthErrorMessage(authErrorCode) || EMAIL_SIGN_UP_ERROR,
                authError.code,
            );
        }
    }

    return new Promise((resolve, reject) => {
        signUpWithEmail({
            credentials: {
                email,
                password,
            },
            onComplete: (currentUser) => {
                resolve(currentUser);
            },
            onError: (error) => {
                const authErrorCode = error.code as AUTHENTICATION_ERROR_CODE;
                if (
                    authErrorCode === AUTHENTICATION_ERROR_CODE.emailAlreadyInUse
                    || authErrorCode === AUTHENTICATION_ERROR_CODE.emailAlreadyExists
                ) {
                    getExistingEmailSignInGuidance({
                        email,
                        fallback: EMAIL_SIGN_UP_EMAIL_IN_USE,
                    }).then((existingEmailMessage) => {
                        reject(createAuthFlowError<IAuthFlowError>(existingEmailMessage, error.code));
                    });
                    return;
                }
                const message = getAuthErrorMessage(authErrorCode)
                    || EMAIL_SIGN_UP_ERROR;
                reject(createAuthFlowError<IAuthFlowError>(message, error.code));
            },
        });
    });
};

const authenticateWithEmailCredentials = async ({
    test,
    mode,
    hasSound,
    email,
    password,
    oldUserData,
}: {
    test: boolean,
    mode: EMAIL_CREDENTIALS_AUTH_MODE,
    hasSound: boolean,
    email: string,
    password: string,
    oldUserData?: IAuthenticatedUserSetupOldUserData,
}): Promise<User | null> => {
    if (mode === EMAIL_CREDENTIALS_AUTH_MODE.signUp) {
        return signUpWithEmailCredentials({
            test,
            email,
            password,
            oldUserData,
        });
    }

    return signInWithEmailCredentials({
        test,
        hasSound,
        email,
        password,
        oldUserData,
    });
};

export default authenticateWithEmailCredentials;
